"use client";

import { useEffect, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import type { LifestylePrize } from "@/app/redux/features/api/lifestyle.types";
import { getCookie } from "@/helpers/cookies";
import type { HeroPrize } from "../../hooks/useLifestyleCollections";
import FeaturedSlide from "./FeaturedSlide";
import HowItWorksSteps from "./HowItWorksSteps";
import { ChevronLeftIcon, ChevronRightIcon } from "../lifestyleIcons";

interface PickYourPrizeHeroProps {
  prizes: HeroPrize[];
  onAddToCart: (prize: LifestylePrize) => void;
  /** Opens the mobile "how it works" sheet, which the page owns. */
  onOpenHowItWorks: () => void;
}

/** Dots are capped at 5 in the design; hide them entirely past that. */
const MAX_DOTS = 5;

/**
 * Page header and featured-prize carousel.
 *
 * Embla drives the track so the slides can be swiped and dragged rather than
 * only stepped through with the arrows. Its `watchSlides` default reinitialises
 * the carousel when the ITH banner list changes.
 *
 * Rotation is manual only (Figma: loops, no auto-rotate).
 *
 * Logged-in users hide the how-it-works section under the hero on mobile only;
 * desktop still shows the steps card. On mobile the explainer is a link inside
 * each slide.
 */
export default function PickYourPrizeHero({ prizes, onAddToCart, onOpenHowItWorks }: PickYourPrizeHeroProps) {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true });
  const [index, setIndex] = useState(0);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const total = prizes.length;
  const showDots = total > 1 && total <= MAX_DOTS;

  useEffect(() => {
    setIsLoggedIn(getCookie("BotB.Authenticated") === "true");
  }, []);

  useEffect(() => {
    if (!emblaApi) return;

    const onSelect = () => setIndex(emblaApi.selectedScrollSnap());
    onSelect();
    emblaApi.on("select", onSelect).on("reInit", onSelect);

    return () => {
      emblaApi.off("select", onSelect).off("reInit", onSelect);
    };
  }, [emblaApi]);

  return (
    // `body` defaults to Roboto, so the section opts the whole tree into Jost.
    <section className="flex flex-col w-full font-jost bg-gray-3">
      <div className="flex flex-col items-center justify-center w-full px-4 py-3 lg:py-6 gap-2">
        <h1 className="text-xl lg:text-[32px] font-semibold leading-[26px] lg:leading-10 uppercase text-primary !mb-0">
          Pick your prize
        </h1>
        <p className="max-w-[320px] lg:max-w-none text-sm lg:text-2xl font-bold leading-5 lg:leading-[30px] tracking-[0] text-secondary text-center">
          Only at BOTB: pick from 200+ prizes and play for yours.
        </p>
      </div>

      {total > 0 && (
        <div className="relative flex flex-col w-full bg-black">
          <div ref={emblaRef} className="w-full overflow-hidden">
            <div className="flex touch-pan-y select-none">
              {prizes.map((prize, slideIndex) => (
                <div key={prize.ContentNodeId} className="flex-[0_0_100%] min-w-0">
                  <FeaturedSlide
                    prize={prize}
                    onAddToCart={onAddToCart}
                    eager={slideIndex === 0}
                    showHowItWorksLink={isLoggedIn}
                    onOpenHowItWorks={onOpenHowItWorks}
                  />
                </div>
              ))}
            </div>
          </div>

          {/*
           * Mobile dots sit in this strip. When the how-it-works card is shown
           * it overlaps the strip; when logged in the strip is just pagination.
           */}
          <div className="lg:hidden h-10 w-full bg-black" aria-hidden="true" />

          {total > 1 && (
            <>
              <button
                type="button"
                onClick={() => emblaApi?.scrollPrev()}
                aria-label="Previous featured prize"
                className="hidden lg:flex items-center justify-center absolute top-1/2 left-2.5 -translate-y-1/2 w-10 h-10 text-white bg-[rgba(46,46,46,0.6)] rounded-full hover:bg-[rgba(46,46,46,0.8)] transition-colors">
                <ChevronLeftIcon className="w-6 h-6" />
              </button>
              <button
                type="button"
                onClick={() => emblaApi?.scrollNext()}
                aria-label="Next featured prize"
                className="hidden lg:flex items-center justify-center absolute top-1/2 right-2.5 -translate-y-1/2 w-10 h-10 text-white bg-[rgba(46,46,46,0.6)] rounded-full hover:bg-[rgba(46,46,46,0.8)] transition-colors">
                <ChevronRightIcon className="w-6 h-6" />
              </button>

              {showDots && (
                <div
                  className={`flex items-center absolute left-1/2 ${
                    isLoggedIn ? "bottom-5 lg:bottom-20" : "bottom-14 lg:bottom-20"
                  } -translate-x-1/2 gap-2 z-[1]`}>
                  {prizes.map((prize, dotIndex) => (
                    <button
                      key={prize.ContentNodeId}
                      type="button"
                      onClick={() => emblaApi?.scrollTo(dotIndex)}
                      aria-label={`Show featured prize ${dotIndex + 1} of ${total}`}
                      aria-current={dotIndex === index}
                      className={`rounded-full transition-colors ${
                        dotIndex === index
                          ? "w-2.5 h-2.5 lg:w-3.5 lg:h-3.5 bg-primary"
                          : "w-2 h-2 lg:w-2.5 lg:h-2.5 bg-white/50 hover:bg-white/80"
                      }`}
                    />
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/*
       * Slight overlap with the banner (Figma ~49px on desktop). Hidden on mobile
       * when logged in (slide link replaces it). The negative margin is only
       * applied when there is a banner to overlap — otherwise the cards would
       * ride up over the title instead.
       */}
      <div
        className={`relative z-10 w-full ${isLoggedIn ? "hidden lg:block" : ""} ${total > 0 ? "-mt-10 lg:-mt-12" : ""} px-5 pb-6`}>
        <div className="w-full max-w-[1320px] mx-auto">
          <HowItWorksSteps onOpenDetails={onOpenHowItWorks} />
        </div>
      </div>
    </section>
  );
}
